import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { MiConsultorio } from "../components/MiConsultorio";
import { BarChart3, Users, Stethoscope, Baby, TrendingUp, Clock, Wallet } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Area, AreaChart,
} from "recharts";

const fade = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };
const money = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(n || 0);
const monthLabel = (m) => {
  try { const [y, mo] = m.split("-"); return new Date(y, mo - 1, 1).toLocaleDateString("es-MX", { month: "short", year: "2-digit" }); }
  catch { return m; }
};

function Card({ children, delay = 0, testid }) {
  return (
    <motion.div {...fade} transition={{ delay }} data-testid={testid}
      className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(61,64,91,0.04)] border border-stone-100">
      {children}
    </motion.div>
  );
}

function EmptyBox({ text }) {
  return <div className="py-10 text-center text-sm text-muted-foreground">{text}</div>;
}

const RANGES = [
  { key: "all", label: "Todo" },
  { key: "1m", label: "Este mes", months: 1 },
  { key: "3m", label: "3 meses", months: 3 },
  { key: "6m", label: "6 meses", months: 6 },
  { key: "12m", label: "12 meses", months: 12 },
];

export default function Statistics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("all");

  const loadStats = () => {
    setLoading(true);
    const params = {};
    const r = RANGES.find((x) => x.key === range);
    if (r?.months) {
      const start = new Date();
      start.setMonth(start.getMonth() - r.months + 1);
      start.setDate(1); start.setHours(0, 0, 0, 0);
      params.start = start.toISOString();
    }
    api.get("/stats", { params }).then((res) => setData(res.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { loadStats(); }, [range]); // eslint-disable-line

  const fee = data?.consultation_fee || 0;
  const incomeTrend = (data?.trend || []).map((t) => ({ m: monthLabel(t.month), income: Math.round(t.count * fee), count: t.count }));

  const kpis = data ? [
    { label: "Pacientes", value: data.patients_total, icon: Users, color: "bg-brand-terracotta/10 text-brand-terracotta" },
    { label: "Consultas", value: data.consultations_total, icon: Stethoscope, color: "bg-brand-sage/15 text-brand-sageDark" },
    { label: "Tiempo x consulta", value: data.avg_consultation_min ? `${data.avg_consultation_min} min` : "—", icon: Clock, color: "bg-blue-50 text-blue-600" },
    { label: "Edad promedio", value: data.avg_age_text, icon: Baby, color: "bg-brand-sand/30 text-amber-700" },
  ] : [];

  return (
    <div className="space-y-8">
      <motion.div {...fade}>
        <p className="text-brand-sage font-bold text-sm tracking-wide uppercase flex items-center gap-2"><BarChart3 size={16} /> ClinPed Analytics</p>
        <h1 className="font-outfit text-3xl sm:text-4xl font-extrabold text-brand-ink tracking-tight mt-1">Tu consulta en números</h1>
        <p className="text-muted-foreground mt-1">Motivos frecuentes, diagnósticos comunes, tiempo de consulta, edad promedio y tendencia mensual.</p>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="stats-range">
          {RANGES.map((r) => (
            <button key={r.key} data-testid={`stats-range-${r.key}`} onClick={() => setRange(r.key)}
              className={`px-3.5 py-1.5 rounded-full text-sm font-semibold transition-all ${
                range === r.key ? "bg-brand-terracotta text-white shadow" : "bg-white border border-stone-200 text-brand-ink/70 hover:border-brand-terracotta/40"
              }`}>{r.label}</button>
          ))}
        </div>
      </motion.div>

      {loading ? (
        <div className="py-16 flex justify-center"><div className="w-8 h-8 rounded-full border-[3px] border-brand-sage/30 border-t-brand-terracotta animate-spin" /></div>
      ) : (
        <>
          <MiConsultorio stats={data} onSaved={loadStats} delay={0.06} />
          {!data || data.consultations_total === 0 ? (
            <Card testid="stats-empty"><EmptyBox text="Aún no hay datos suficientes. Registra consultas para ver tus estadísticas." /></Card>
          ) : (
            <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="stats-kpis">
            {kpis.map((s, i) => (
              <motion.div key={s.label} {...fade} transition={{ delay: i * 0.06 }}
                className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(61,64,91,0.04)] border border-stone-100">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${s.color}`}><s.icon size={24} /></div>
                <p className="font-outfit text-3xl font-extrabold text-brand-ink mt-4">{s.value}</p>
                <p className="text-muted-foreground text-sm font-semibold">{s.label}</p>
              </motion.div>
            ))}
          </div>

          <Card delay={0.09} testid="stats-income">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
              <h2 className="font-outfit text-xl font-bold text-brand-ink flex items-center gap-2"><Wallet size={20} className="text-brand-sageDark" /> Ingresos por mes</h2>
              {!fee && <span className="text-xs text-muted-foreground">Define tu costo de consulta arriba para ver montos</span>}
            </div>
            {incomeTrend.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={incomeTrend} margin={{ left: -5, right: 10 }}>
                  <defs>
                    <linearGradient id="incGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#7A9B76" stopOpacity={0.55} />
                      <stop offset="100%" stopColor="#7A9B76" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="m" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} width={70} tickFormatter={(v) => money(v)} />
                  <Tooltip formatter={(v, n) => n === "income" ? [money(v), "Ingresos"] : [v, "Consultas"]} />
                  <Area type="monotone" dataKey="income" name="income" stroke="#5f7d5b" strokeWidth={3} fill="url(#incGrad)" dot={{ r: 3 }} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <EmptyBox text="Sin datos de ingresos todavía." />}
          </Card>

          <Card delay={0.1} testid="stats-trend">
            <h2 className="font-outfit text-xl font-bold text-brand-ink flex items-center gap-2 mb-5"><TrendingUp size={20} className="text-brand-sage" /> Consultas por mes</h2>
            {data.trend?.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.trend.map((t) => ({ ...t, m: monthLabel(t.month) }))} margin={{ left: -20, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="m" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" name="Consultas" stroke="#C4623F" strokeWidth={3} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : <EmptyBox text="Sin datos de tendencia." />}
          </Card>

          <div className="grid lg:grid-cols-2 gap-5">
            <Card delay={0.15} testid="stats-reasons">
              <h2 className="font-outfit text-xl font-bold text-brand-ink mb-5">Motivos de consulta más frecuentes</h2>
              {data.reasons?.length ? (
                <ResponsiveContainer width="100%" height={Math.max(200, data.reasons.length * 42)}>
                  <BarChart data={data.reasons} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                    <YAxis type="category" dataKey="label" width={140} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" name="Consultas" fill="#7A9B76" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <EmptyBox text="Aún no capturas motivos de consulta." />}
            </Card>

            <Card delay={0.2} testid="stats-diagnoses">
              <h2 className="font-outfit text-xl font-bold text-brand-ink mb-5">Diagnósticos más comunes</h2>
              {data.diagnoses?.length ? (
                <ResponsiveContainer width="100%" height={Math.max(200, data.diagnoses.length * 42)}>
                  <BarChart data={data.diagnoses} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                    <YAxis type="category" dataKey="label" width={140} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" name="Consultas" fill="#C4623F" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <EmptyBox text="Aún no registras diagnósticos." />}
            </Card>
          </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
